import { getEnumMemberName } from 'utils/helpers'
import { parseOrRaw } from 'domain/shared/validation'
import { AssetClass, integrationDetailsResponseSchema } from './schemas'

const GATING_FIELDS = [
  'invitation_status',
  'billing_valid',
  'identity_verification_status',
  'identity_verification_action_url',
]

function capitalize(value) {
  return value ? value.charAt(0).toUpperCase() + value.slice(1) : value
}

function mapIntegrationDetail(integration) {
  if (!integration.asset_class || !integration.platform) return null

  const platform = getEnumMemberName(integration.platform)
  const assetClass = getEnumMemberName(integration.asset_class)
  const extraData = integration.extra_data ?? {}

  // Pre-flight gating fields live top-level on enrich responses, nested
  // under extra_data on list responses (see schemas.js) — normalize to
  // top-level regardless of source. An explicit top-level value (including
  // enrich's `null`) wins over extra_data.
  const gating = Object.fromEntries(
    GATING_FIELDS.map((key) => [key, key in integration ? integration[key] : extraData[key]]),
  )

  // Old v3 behavior defaulted a missing label to 'Facebook' for pixel/
  // ad_account assets — a Facebook-only assumption that silently mislabels
  // Google assets now that they flow through the same shape. Default to the
  // asset's own platform instead.
  const assetLabel =
    integration.asset_label ||
    ([AssetClass.pixel, AssetClass.ad_account].includes(assetClass) ? capitalize(platform) : '')

  return {
    ...integration,
    ...gating,
    platform,
    asset_class: assetClass,
    asset_label: assetLabel,
    connected_assets: (integration.connected_assets ?? []).map((connectedAsset) => ({
      ...connectedAsset,
      platform: getEnumMemberName(connectedAsset.platform),
      asset_class: getEnumMemberName(connectedAsset.asset_class),
    })),
    asset_users: (integration.asset_users ?? []).map((user) => ({
      ...user,
      token_type: getEnumMemberName(user.token_type),
    })),
  }
}

// v4 response -> FE domain model, replacing the old parsePartnerIntegrations.
// Covers Facebook and Google records from the same array (unified endpoint),
// or a single-platform array from a platform-scoped endpoint.
export function mapIntegrationDetails(data) {
  const parsed = parseOrRaw(integrationDetailsResponseSchema, data)
  const source = Array.isArray(parsed) ? parsed : []

  return source.map(mapIntegrationDetail).filter(Boolean)
}

// Multi-platform integration_details need both platform and asset_class to
// identify a record uniquely — asset_class alone (e.g. 'ad_account') matches
// both a Facebook and a Google entry.
export function findByAssetClass(integrationDetails, { platform, assetClass }) {
  return (integrationDetails ?? []).find(
    (x) => x.platform === platform && x.asset_class === assetClass,
  )
}
